/**
 * fab-portrait-core.js
 *
 * Pure helpers for the personalised-portrait PDP (e.g. Dogue). No DOM, no
 * network — everything here is unit-testable under Node and shared by
 * fab-portrait-controller.js in the browser.
 *
 * Contract values (property keys, backend defaults, swatch names) are fixed in
 * English on purpose: they travel to Shopify line items and Supabase and must
 * not change with the storefront locale. Only display labels are translated.
 */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.FabPortraitCore = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var DEFAULT_COVER_TITLE = 'DOGUE';
  var DEFAULT_BACKGROUND = "Creative Director's choice";
  var MAX_COVER_TITLE_CHARS = 30;
  var MAX_BACKGROUND_CHARS = 100;
  var MAX_ORIGINAL_BYTES = 25 * 1024 * 1024;
  var MAX_PREVIEW_BYTES = 2 * 1024 * 1024;
  var MAX_PREVIEW_EDGE = 1200;

  var SUPPORTED_MIME = ['image/jpeg', 'image/png', 'image/heic', 'image/heif', 'image/webp'];
  var EXT_TO_MIME = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    heic: 'image/heic',
    heif: 'image/heif',
    hif: 'image/heif',
  };

  /** Swatch keys are stable ids; `value` is what the line item carries. */
  var BACKGROUND_SWATCHES = [
    { key: 'peach', value: 'Peach', hex: '#F4C7A8' },
    { key: 'pale_pink', value: 'Pale pink', hex: '#F3D2D8' },
    { key: 'sky_blue', value: 'Sky blue', hex: '#BFD9EE' },
    { key: 'sage', value: 'Sage', hex: '#C3CEB9' },
  ];

  var STORAGE_PREFIX = 'fab_portrait_config_v1:';
  var STORAGE_PREFIX_V2 = 'fab_portrait_config_v2:';

  function countChars(str) {
    // Count code points, matching the backend's [...str].length check.
    return Array.from(String(str || '')).length;
  }

  function hasControlChars(str) {
    return /[\u0000-\u001f\u007f]/.test(String(str || ''));
  }

  /**
   * Cover title as submitted. Blank/whitespace → DOGUE; otherwise the entered
   * text, trimmed only at the ends (never re-spelt or re-cased).
   */
  function normaliseCoverTitle(raw) {
    var trimmed = String(raw == null ? '' : raw).trim();
    if (!trimmed) return { value: DEFAULT_COVER_TITLE, valid: true, isDefault: true };
    var valid = countChars(trimmed) <= MAX_COVER_TITLE_CHARS && !hasControlChars(trimmed);
    return { value: trimmed, valid: valid, isDefault: false };
  }

  /**
   * Background preference as submitted.
   *   choice: 'director' | swatch key | 'other'
   *   otherText: free text when choice === 'other'
   */
  function resolveBackground(choice, otherText) {
    var c = String(choice || 'director');
    if (c === 'other') {
      var text = String(otherText == null ? '' : otherText).trim();
      if (!text) return { value: DEFAULT_BACKGROUND, valid: true, isDefault: true };
      var valid = countChars(text) <= MAX_BACKGROUND_CHARS && !hasControlChars(text);
      return { value: text, valid: valid, isDefault: false };
    }
    for (var i = 0; i < BACKGROUND_SWATCHES.length; i++) {
      if (BACKGROUND_SWATCHES[i].key === c) {
        return { value: BACKGROUND_SWATCHES[i].value, valid: true, isDefault: false };
      }
    }
    return { value: DEFAULT_BACKGROUND, valid: true, isDefault: true };
  }

  /** Longest edge ≤ MAX_PREVIEW_EDGE, aspect preserved, never upscaled. */
  function computePreviewSize(width, height, maxEdge) {
    var max = maxEdge || MAX_PREVIEW_EDGE;
    var w = Math.max(1, Math.round(Number(width) || 1));
    var h = Math.max(1, Math.round(Number(height) || 1));
    var longest = Math.max(w, h);
    if (longest <= max) return { width: w, height: h };
    var scale = max / longest;
    return {
      width: Math.max(1, Math.round(w * scale)),
      height: Math.max(1, Math.round(h * scale)),
    };
  }

  function extensionOf(name) {
    var m = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return m ? m[1].toLowerCase() : '';
  }

  /**
   * Browsers (iOS in particular) sometimes hand over HEIC files with an empty
   * or generic type. Fall back to the extension so the declared MIME matches
   * what the backend validates against.
   */
  function inferMime(file) {
    var type = String((file && file.type) || '').toLowerCase().trim();
    if (SUPPORTED_MIME.indexOf(type) >= 0) return type;
    var ext = extensionOf(file && file.name);
    if (EXT_TO_MIME[ext]) return EXT_TO_MIME[ext];
    return type;
  }

  function isSupportedMime(mime) {
    return SUPPORTED_MIME.indexOf(String(mime || '').toLowerCase()) >= 0;
  }

  function isHeicMime(mime) {
    var m = String(mime || '').toLowerCase();
    return m === 'image/heic' || m === 'image/heif';
  }

  /**
   * Validate a picked file before any decoding. Returns { ok, error, mime }.
   * error ∈ 'unsupported_type' | 'too_large'
   */
  function validateFile(file) {
    if (!file) return { ok: false, error: 'unsupported_type', mime: '' };
    var mime = inferMime(file);
    if (!isSupportedMime(mime)) return { ok: false, error: 'unsupported_type', mime: mime };
    if (Number(file.size) > MAX_ORIGINAL_BYTES) return { ok: false, error: 'too_large', mime: mime };
    return { ok: true, error: null, mime: mime };
  }

  function uuid() {
    var c = (typeof crypto !== 'undefined' && crypto) || (typeof self !== 'undefined' && self.crypto);
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    if (c && typeof c.getRandomValues === 'function') {
      var bytes = new Uint8Array(16);
      c.getRandomValues(bytes);
      bytes[6] = (bytes[6] & 0x0f) | 0x40;
      bytes[8] = (bytes[8] & 0x3f) | 0x80;
      var hex = Array.prototype.map.call(bytes, function (b) { return ('0' + b.toString(16)).slice(-2); }).join('');
      return hex.slice(0, 8) + '-' + hex.slice(8, 12) + '-' + hex.slice(12, 16) + '-' + hex.slice(16, 20) + '-' + hex.slice(20);
    }
    // Last resort (non-secure contexts): RFC4122-shaped from Math.random.
    var s = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (ch) {
      var r = (Math.random() * 16) | 0;
      var v = ch === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
    return s;
  }

  var UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  function isUuid(v) {
    return UUID_RE.test(String(v || ''));
  }

  /**
   * The configuration identity is the (photo, cover title, background) triple.
   * Size/frame are variant options and do not take part.
   */
  function configFingerprint(state) {
    state = state || {};
    var upload = state.upload || {};
    return [
      upload.uploadId || '',
      normaliseCoverTitle(state.coverTitle).value,
      resolveBackground(state.backgroundChoice, state.backgroundOther).value,
    ].join('\u001f');
  }

  /**
   * Keep `_customisation_id` stable while the fingerprint is unchanged
   * (retries, refresh, size/frame changes); mint a new one when it changes.
   * Returns a shallow copy with customisationId + fingerprint set.
   *
   * Pass `{ universal: true }` for product-authored fields (values + uploads
   * + config_hash). Size/frame still do not take part.
   */
  function ensureCustomisationId(state, opts) {
    var next = Object.assign({}, state || {});
    var fp = opts && opts.universal ? personalisationFingerprint(next) : configFingerprint(next);
    if (!next.customisationId || !isUuid(next.customisationId) || next.fingerprint !== fp) {
      next.customisationId = uuid();
      next.fingerprint = fp;
    }
    return next;
  }

  function storageKey(productId) {
    return STORAGE_PREFIX + String(productId || 'unknown');
  }

  function storageKeyV2(productId) {
    return STORAGE_PREFIX_V2 + String(productId || 'unknown');
  }

  /**
   * Stable identity of the Liquid-rendered / GET-returned field list.
   * Keys and types only — labels are locale-specific and must not take part.
   */
  function fieldSchemaFingerprint(fields) {
    return (Array.isArray(fields) ? fields : [])
      .map(function (f) {
        if (!f || !f.key) return '';
        return String(f.key) + ':' + String(f.type || 'text');
      })
      .filter(Boolean)
      .sort()
      .join('|');
  }

  function schemasMatch(liquidFields, remoteFields) {
    var a = fieldSchemaFingerprint(liquidFields);
    var b = fieldSchemaFingerprint(remoteFields);
    return !!a && a === b;
  }

  /**
   * Universal-flow fingerprint: config schema + customer values + upload ids.
   * `values` must already be the strings sent to prepare-personalisation.
   */
  function personalisationFingerprint(state) {
    state = state || {};
    var values = state.values && typeof state.values === 'object' ? state.values : {};
    var uploads = state.uploads && typeof state.uploads === 'object' ? state.uploads : {};
    var valuePart = Object.keys(values).sort().map(function (k) {
      return k + '=' + String(values[k] == null ? '' : values[k]);
    }).join('\u001f');
    var uploadPart = Object.keys(uploads).sort().map(function (k) {
      var u = uploads[k] || {};
      return k + '=' + (u.uploadId || '');
    }).join('\u001f');
    return [state.configHash || '', valuePart, uploadPart].join('\u001e');
  }

  function uploadsPayload(uploads) {
    var out = {};
    Object.keys(uploads || {}).forEach(function (key) {
      var u = uploads[key];
      if (u && isUuid(u.uploadId) && u.receipt) {
        out[key] = { upload_id: u.uploadId, upload_receipt: u.receipt };
      }
    });
    return out;
  }

  /** Slot is purchasable once screened and we hold id + receipt. Never require a staff download URL. */
  function slotIsReady(upload) {
    if (!upload || !upload.verified) return false;
    return !!(isUuid(upload.uploadId) && upload.receipt);
  }

  /** Persistable upload record — never includes staff download URLs. */
  function sanitiseUploadRecord(upload) {
    if (!upload) return null;
    return {
      uploadId: upload.uploadId || '',
      receipt: upload.receipt || '',
      previewUrl: upload.previewUrl || '',
      status: upload.status || '',
      fileName: upload.fileName || '',
      verified: !!upload.verified,
      requiresManualReview: !!upload.requiresManualReview,
    };
  }

  /** Draft AI preview is offered only for screened_clean slots, never flagged. */
  function slotIsScreenedClean(upload) {
    if (!upload || !slotIsReady(upload)) return false;
    if (upload.requiresManualReview) return false;
    return upload.status === 'screened_clean';
  }

  function isCustomerPropertyKey(key) {
    return String(key || '').charAt(0) !== '_';
  }

  function colourSwatchesForField(field) {
    var options = field && field.options;
    if (Array.isArray(options) && options.length) {
      return options.map(function (o) {
        if (o && typeof o === 'object') {
          return {
            key: String(o.key || o.value || ''),
            value: String(o.value || o.label || ''),
            hex: String(o.hex || o.colour || o.color || ''),
          };
        }
        return { key: String(o), value: String(o), hex: '' };
      }).filter(function (o) { return o.value; });
    }
    return BACKGROUND_SWATCHES.slice();
  }

  /**
   * Colour preference as submitted to prepare-personalisation.
   *   choice: '__director__' | swatch English value | '__other__'
   * Blank / director → field.default (or empty string). Never invent a promise.
   */
  function resolveColourValue(choice, otherText, field) {
    var def = field && field.default != null ? String(field.default) : '';
    var max = Number(field && field.max_length) || MAX_BACKGROUND_CHARS;
    var c = String(choice || '__director__');
    if (c === '__director__' || c === 'director' || !c) {
      return { value: def, valid: true, isDefault: true };
    }
    if (c === '__other__' || c === 'other') {
      var text = String(otherText == null ? '' : otherText).trim();
      if (!text) return { value: def, valid: true, isDefault: true };
      var valid = countChars(text) <= max && !hasControlChars(text);
      return { value: text, valid: valid, isDefault: false };
    }
    var swatches = colourSwatchesForField(field);
    for (var i = 0; i < swatches.length; i++) {
      if (swatches[i].key === c || swatches[i].value === c) {
        return { value: swatches[i].value, valid: true, isDefault: false };
      }
    }
    var typed = String(c).trim();
    if (!typed) return { value: def, valid: true, isDefault: true };
    return {
      value: typed,
      valid: countChars(typed) <= max && !hasControlChars(typed),
      isDefault: false,
    };
  }

  function validateFieldValue(field, raw) {
    field = field || {};
    var type = String(field.type || 'text');
    var required = !!field.required;
    var max = Number(field.max_length) || (type === 'textarea' ? 2000 : 200);
    var value = String(raw == null ? '' : raw).trim();
    if (!value) {
      return { value: '', valid: !required, empty: true };
    }
    if (hasControlChars(value) && type !== 'textarea') {
      return { value: value, valid: false, empty: false };
    }
    if (type !== 'date' && countChars(value) > max) {
      return { value: value, valid: false, empty: false };
    }
    return { value: value, valid: true, empty: false };
  }

  function collectValuesFromState(fields, values) {
    var out = {};
    values = values || {};
    (Array.isArray(fields) ? fields : []).forEach(function (f) {
      if (!f || !f.key || f.type === 'image') return;
      out[f.key] = values[f.key] == null ? '' : String(values[f.key]);
    });
    return out;
  }

  function parsePrepareError(httpStatus, body) {
    var err = (body && body.error) || '';
    if (httpStatus === 409 || err === 'configuration_changed') return 'configuration_changed';
    if (err === 'upload_configuration_mismatch') return 'upload_configuration_mismatch';
    return err || 'prepare_failed';
  }

  /** Upload record is purchasable only with every returned value present. */
  function uploadHasRequiredValues(upload) {
    if (!upload) return false;
    return !!(isUuid(upload.uploadId) && upload.receipt && upload.previewUrl && upload.originalDownloadUrl);
  }

  /**
   * Merge a status/upload response body into an upload record. Late responses
   * for a different upload_id are ignored (returns the record unchanged).
   */
  function mergeUploadResponse(upload, body) {
    if (!upload || !body) return upload;
    if (body.upload_id && upload.uploadId && body.upload_id !== upload.uploadId) return upload;
    var next = Object.assign({}, upload);
    if (body.upload_id) next.uploadId = body.upload_id;
    if (body.upload_receipt) next.receipt = body.upload_receipt;
    if (body.preview_url) next.previewUrl = body.preview_url;
    if (body.original_download_url) next.originalDownloadUrl = body.original_download_url;
    if (body.status) next.status = body.status;
    if (typeof body.requires_manual_review === 'boolean') next.requiresManualReview = body.requires_manual_review;
    return next;
  }

  /** Line-item properties for the current state (assumes it has been validated). */
  function buildLineItemProperties(state, opts) {
    opts = opts || {};
    var upload = (state && state.upload) || {};
    var title = normaliseCoverTitle(state && state.coverTitle).value;
    var background = resolveBackground(state && state.backgroundChoice, state && state.backgroundOther).value;
    var props = {
      'Cover title': title,
      'Background colour': background,
      _mode: 'portrait',
      _style_key: opts.styleKey || 'dogue',
      _style_version: String(opts.styleVersion || '1'),
      _source_product_id: String(opts.sourceProductId || ''),
      _customisation_id: state && state.customisationId,
      _upload_id: upload.uploadId || '',
      _upload_receipt: upload.receipt || '',
      _preview_url: upload.previewUrl || '',
      _original_download_url: upload.originalDownloadUrl || '',
      _orientation: 'portrait',
    };
    if (opts.frameCode) props._frame_code = opts.frameCode;
    return props;
  }

  return {
    DEFAULT_COVER_TITLE: DEFAULT_COVER_TITLE,
    DEFAULT_BACKGROUND: DEFAULT_BACKGROUND,
    MAX_COVER_TITLE_CHARS: MAX_COVER_TITLE_CHARS,
    MAX_BACKGROUND_CHARS: MAX_BACKGROUND_CHARS,
    MAX_ORIGINAL_BYTES: MAX_ORIGINAL_BYTES,
    MAX_PREVIEW_BYTES: MAX_PREVIEW_BYTES,
    MAX_PREVIEW_EDGE: MAX_PREVIEW_EDGE,
    SUPPORTED_MIME: SUPPORTED_MIME,
    BACKGROUND_SWATCHES: BACKGROUND_SWATCHES,
    countChars: countChars,
    hasControlChars: hasControlChars,
    normaliseCoverTitle: normaliseCoverTitle,
    resolveBackground: resolveBackground,
    computePreviewSize: computePreviewSize,
    inferMime: inferMime,
    isSupportedMime: isSupportedMime,
    isHeicMime: isHeicMime,
    validateFile: validateFile,
    uuid: uuid,
    isUuid: isUuid,
    configFingerprint: configFingerprint,
    personalisationFingerprint: personalisationFingerprint,
    ensureCustomisationId: ensureCustomisationId,
    storageKey: storageKey,
    storageKeyV2: storageKeyV2,
    fieldSchemaFingerprint: fieldSchemaFingerprint,
    schemasMatch: schemasMatch,
    uploadsPayload: uploadsPayload,
    slotIsReady: slotIsReady,
    sanitiseUploadRecord: sanitiseUploadRecord,
    slotIsScreenedClean: slotIsScreenedClean,
    isCustomerPropertyKey: isCustomerPropertyKey,
    colourSwatchesForField: colourSwatchesForField,
    resolveColourValue: resolveColourValue,
    validateFieldValue: validateFieldValue,
    collectValuesFromState: collectValuesFromState,
    parsePrepareError: parsePrepareError,
    uploadHasRequiredValues: uploadHasRequiredValues,
    mergeUploadResponse: mergeUploadResponse,
    buildLineItemProperties: buildLineItemProperties,
  };
});
